/**
 * Centralised conversion + page-view tracker.
 *
 * Every event is pushed to:
 *  1. window.dataLayer  (for GTM — recommended source of truth)
 *  2. window.gtag        (GA4 + Google Ads if AW- ID is configured)
 *  3. window.fbq         (Meta Pixel)
 *  4. window.clarity     (Microsoft Clarity custom tag)
 *
 * All four are no-ops if the corresponding script hasn't loaded yet,
 * so it's safe to call before tags are ready.
 *
 * Usage:
 *   import { trackEvent, trackPageView, EVENTS } from "@/utils/tracking";
 *   trackEvent(EVENTS.WHATSAPP_CLICK, { location: "footer" });
 */

export const EVENTS = {
  CONTACT_FORM_SUBMIT: "contact_form_submit",
  WHATSAPP_CLICK: "whatsapp_click",
  PHONE_CLICK: "phone_click",
  EMAIL_CLICK: "email_click",
  PLAN_EVENT_CTA_CLICK: "plan_event_cta_click",
  VENUE_INQUIRY_CLICK: "venue_inquiry_click",
  PACKAGE_INQUIRY_CLICK: "package_inquiry_click",
  BROCHURE_DOWNLOAD_CLICK: "brochure_download_click",
  NEWSLETTER_SUBSCRIBE: "newsletter_subscribe",
};

// Map our internal event names to Meta Pixel standard events for richer reporting.
const META_EVENT_MAP = {
  contact_form_submit: { type: "track", name: "Lead" },
  whatsapp_click: { type: "trackCustom", name: "WhatsAppClick" },
  phone_click: { type: "track", name: "Contact" },
  email_click: { type: "track", name: "Contact" },
  plan_event_cta_click: { type: "trackCustom", name: "PlanEventCTA" },
  venue_inquiry_click: { type: "trackCustom", name: "VenueInquiry" },
  package_inquiry_click: { type: "trackCustom", name: "PackageInquiry" },
  brochure_download_click: { type: "trackCustom", name: "BrochureDownload" },
  newsletter_subscribe: { type: "track", name: "Subscribe" },
};

const isBrowser = () => typeof window !== "undefined";

const safeDataLayer = (payload) => {
  if (!isBrowser()) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
};

const safeGtag = (...args) => {
  if (!isBrowser() || typeof window.gtag !== "function") return;
  try { window.gtag(...args); } catch (e) { /* swallow */ }
};

const safeFbq = (type, name, params) => {
  if (!isBrowser() || typeof window.fbq !== "function") return;
  try {
    if (params) window.fbq(type, name, params);
    else window.fbq(type, name);
  } catch (e) { /* swallow */ }
};

const safeClarity = (...args) => {
  if (!isBrowser() || typeof window.clarity !== "function") return;
  try { window.clarity(...args); } catch (e) { /* swallow */ }
};

/**
 * Generic conversion event.
 * @param {string} eventName  one of EVENTS.*
 * @param {object} params     optional parameters (location, value, currency, etc.)
 */
export function trackEvent(eventName, params = {}) {
  if (!eventName) return;
  const payload = { event: eventName, ...params };

  // 1. dataLayer for GTM — preferred routing source
  safeDataLayer(payload);

  // 2. GA4 direct (in addition to GTM, in case GTM doesn't have the GA4 tag mapped)
  safeGtag("event", eventName, params);

  // 3. Google Ads conversion — fires only if conversion id+label provided in params
  if (params.send_to) {
    safeGtag("event", "conversion", { send_to: params.send_to, ...params });
  }

  // 4. Meta Pixel — map to standard or custom event
  const meta = META_EVENT_MAP[eventName];
  if (meta) {
    safeFbq(meta.type, meta.name, sanitizeForFb(params));
  } else {
    safeFbq("trackCustom", eventName, sanitizeForFb(params));
  }

  // 5. Microsoft Clarity custom tag (helps segment recordings)
  safeClarity("event", eventName);
  if (params.location) safeClarity("set", eventName, params.location);
}

function sanitizeForFb(params) {
  // Only forward primitive-friendly fields to Meta to avoid PII rejection.
  const out = {};
  ["location", "value", "currency", "content_name", "content_category", "event_type", "city"].forEach((k) => {
    if (params[k] !== undefined && params[k] !== null && params[k] !== "") out[k] = params[k];
  });
  return out;
}

/**
 * SPA route-change page view.
 * Pushes a virtual pageview into dataLayer (for GTM) and config to GA4.
 */
export function trackPageView(path, title) {
  if (!isBrowser()) return;
  const url = path || (window.location.pathname + window.location.search);
  const docTitle = title || document.title;

  safeDataLayer({
    event: "page_view",
    page: { path: url, title: docTitle, location: window.location.href },
  });

  safeGtag("event", "page_view", {
    page_path: url,
    page_title: docTitle,
    page_location: window.location.href,
  });

  safeFbq("track", "PageView");
}
